import type { EmbeddingValue } from '../embedding/embedding.interface';
import type { RetrievalClassificationHints } from '../rag/fact-query.interface';

export interface TenantContext {
  tenantId: string;
  userId: string;
  sessionId?: string;
  permissions?: string[];
}

// ---------------------------------------------------------------------------
// Goal-Setting & Pursuit Framework
// ---------------------------------------------------------------------------

export type GoalScope = 'org' | 'user';
export type GoalType =
  | 'behavioral'
  | 'cognitive'
  | 'affective'
  | 'relational'
  | 'transformational';
export type GoalInitiative = 'companion' | 'responsive' | 'active';
export type GoalPursuitPreference = 'default' | 'companion_only';
export type GoalUserOverride = 'default_org_goal' | 'custom_goal' | 'companion_only';
export type GoalTimeHorizon = 'single_chat' | '30_days' | '90_days' | 'ongoing';
export type GoalUserVisibility = 'full' | 'summary' | 'hidden';
export type GoalLifecycleStatus = 'draft' | 'active' | 'paused' | 'expired' | 'archived';
export type GoalDecision =
  | 'actively_pursue'
  | 'lightly_include'
  | 'keep_in_background'
  | 'pause_for_now';
export type GoalAuditAction = 'create' | 'update' | 'pause' | 'archive' | 'delete' | 'attach' | 'detach';
export type GoalRuntimeMode = 'background' | 'tracked';
export type GoalRuntimeFervency = 'background' | 'gentle' | 'active' | 'focused' | 'strict';
export type GoalPolicyRole = 'primary' | 'supporting';

export interface GoalMethodology {
  style: 'socratic' | 'didactic' | 'narrative' | 'exemplary' | 'counseling';
  anchorSource?: 'scripture' | 'founder_texts' | 'personal_history' | 'medical_lit' | 'none';
  pacing: 'gradual' | 'direct' | 'responsive_only';
  voice: 'companion' | 'mentor' | 'teacher' | 'peer' | 'pastor';
  questionDensity: 'high' | 'medium' | 'low';
  personalizationDepth: 'none' | 'low' | 'medium' | 'high';
}

export interface Goal {
  id: string;
  /** Native v2 policy-view identity. These fields are never persisted as a second goal record. */
  definitionId?: string;
  assignmentId?: string;
  definitionVersion?: string;
  executionKind?: 'prompt_only' | 'criteria' | 'atlas' | 'metric';
  scope: GoalScope;
  owner: string | string[];
  name: string;
  alignmentStatement: string;
  goalTypes: GoalType[];
  targetSignal?: string;
  successCriteria?: string[];
  timeHorizon: GoalTimeHorizon;
  methodology: GoalMethodology;
  methodologyPresetId?: string;
  /** Atlas-only learning mode projected from the assignment delivery policy. */
  learningMode?: 'teach_and_assess' | 'assess_only';
  /**
   * Most goals are background prompt-policy goals. Tracked goals additionally run a
   * runtime spec that records criteria, evidence, next tactic, and observability.
   */
  runtimeMode?: GoalRuntimeMode;
  runtimeSpecId?: string;
  runtimeFervency?: GoalRuntimeFervency;
  initiativeLevel: GoalInitiative;
  /** Native v2 policy limits. Legacy goals fall back to policy defaults. */
  mentionCap?: number;
  cooldownTurns?: number;
  resistanceLimit?: number;
  priorityWeight: number;
  topicBoundaries: string[];
  expiry?: string;
  userVisibility: GoalUserVisibility;
  lifecycleStatus: GoalLifecycleStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface MethodologyPreset {
  id: string;
  name: string;
  description?: string;
  methodology: GoalMethodology;
  createdAt: string;
  updatedAt: string;
}

export interface GoalAuditEvent {
  id: string;
  goalId?: string;
  personaId?: string;
  actorId: string;
  action: GoalAuditAction;
  before?: unknown;
  after?: unknown;
  at: string;
}

export interface OrgTheme {
  name: string;
  alignmentStatement: string;
  themeDescription: string;
  topicBoundaries: string[];
}

export interface PerGoalDecisionTrace {
  goalId: string;
  decision: GoalDecision;
  reasonCodes: string[];
}

export interface GoalPlanningMetadata {
  skipped?: boolean;
  reason?: string;
  message?: string;
  goalId?: string;
  libraryGoalId?: string;
  libraryGoalName?: string;
  policyRole?: GoalPolicyRole;
  move?: unknown;
  observability?: unknown;
  events?: unknown[];
  memoryCandidates?: unknown[];
  /** Wall-clock engine planning cost for the turn in milliseconds. */
  planningMs?: number;
}

export interface GoalDecisionResult {
  decision: GoalDecision;
  reasonCodes: string[];
  /** @deprecated Use primaryGoal. */
  activeGoal?: Goal;
  /** The goal that should be actively pursued this turn. */
  primaryGoal?: Goal;
  /** @deprecated Use primaryGoal. */
  pullGoal?: Goal;
  /** Goals that can quietly shape the answer without being raised directly. */
  supportingGoals: Goal[];
  /** @deprecated Use supportingGoals. */
  weaveGoals?: Goal[];
  inheritedTheme?: OrgTheme;
  initiative: GoalInitiative;
  mentionCapRemaining?: number;
  perGoal: PerGoalDecisionTrace[];
  runtime?: GoalPlanningMetadata;
}

export type PromptComponentId = { id: string; type: string };

export type PromptConfigItem =
  | PromptComponentId
  | { parameterKey: string; mapping: Record<string, PromptComponentId> };

// TODO: Decide if we need an interface to define the statistics (similar to Fic2Bot) we will hold for Persona.
export interface Persona {
  id: string;
  tenantId: string;
  category: string;
  /** Restricts this persona to users in the Cognito `admin` group. */
  adminOnly?: boolean;
  /** Keep the static category authoritative when synchronizing an existing persona. */
  syncCategoryOnStartup?: boolean;
  name: string;
  temperature: number;
  responseMaxTokens: number;
  personality?: string;
  legacy?: boolean; // Flag to indicate if this is a legacy persona definition without modeGuidance
  /** Optional ISO birth date (YYYY-MM-DD) for birthday-aware age computation */
  birthDate?: string;

  /** Core identity: who they are, biographical anchors, ministry journey */
  identity: string;

  /** Audio-linguistic style: staccato bursts, self-correction, direct "you" */
  audioStyle: string;

  /** Theological frameworks they use (r12, BIO, etc.) */
  theologicalFrameworks: string;

  /** Mode-specific guidance injected per responseMode */
  modeGuidance: {
    smallTalk: string;
    straightTalk: string;
    aligned: string;
    caution: string;
    neutralSegue: string;
    blocked: string;
    webSearch?: string;
  };

  /** When true and alignment is unaligned, route to web search generation (if the adapter is registered). */
  enableWebSearch?: boolean;

  /** Optional static/avatar image URL used by frontend profile slots. */
  imageURL?: string;
  avatar_url?: string;

  /** @deprecated Use modeGuidance instead */
  styleDirectives?: string;

  /** Prompt configuration for PromptService */
  promptConfig?: PromptConfigItem[];

  // Companion User Personalization
  companionSettings?: CompanionSettings;

  /**
   * When true, prompt is owned by a protocol/curriculum; companion delivery
   * preferences (e.g. answer length) are never injected into prompt.
   */
  protocolOwned?: boolean;

  /**
   * When true, inject companion-settings prompt overlay (AI preset, guidance lens,
   * answer length). LOTE discipleship personas opt in; teaching personas like
   * Giacomo do not.
   */
  companionSettingsOverlay?: boolean;

  /** Tavus replica ID for video synthesis */
  replicaId?: string;

  /** Optional Tavus persona ID override for DEV environment */
  devPersonaId?: string;

  /** Optional Tavus persona ID override for LOCAL environment */
  localPersonaId?: string;

  /** ElevenLabs voice ID for TTS synthesis */
  voiceId?: string;

  /**
   * Indicates that retrieved knowledge belongs to a different source persona
   * and must be transposed rather than presented as this persona's biography.
   */
  knowledgeTranspositionBasePersona?: string;

  /** Optional Discovery guide profile used when this persona runs in discoveryMode. */
  discoveryGuideProfileId?: string;

  /** Library goal IDs attached to this persona (hydrated at request time). */
  goalIds?: string[];

  /** Master toggle for goal mode. False disables all goal pursuit for this
   * persona (including discovery); true or undefined leaves it to policy. */
  goalsEnabled?: boolean;

  /**
   * @deprecated Use goalIds + Goal library. Kept one release for backward compatibility.
   */
  goal?: Goal;
}

export interface DocumentChunk {
  id: string;
  content: string;
  embedding?: EmbeddingValue;
  metadata: {
    source: string;
    score?: number;
    topic?: string;
    chunkIndex?: number;
    [k: string]: any;
  };
}

export type ConversationTopicStatus = 'unresolved' | 'resolving' | 'resolved';
export type ConversationMomentumState = 'low_momentum' | 'keep_exploring' | 'resolving' | 'likely_resolved';
export type ConversationFollowUpStyle = 'gentle' | 'specific' | 'none';

export interface ConversationPolicy {
  /**
   * Whether the current topic appears unresolved, resolving, or resolved.
   * Intended to guide follow-up questions vs clean closures.
   */
  topicStatus: ConversationTopicStatus;
  /**
   * Pacing / engagement state for the next assistant turn.
   */
  momentumState: ConversationMomentumState;
  /**
   * Whether the assistant should ask a follow-up question this turn.
   */
  shouldAskFollowUp: boolean;
  /**
   * If asking a question, what style it should be.
   */
  followUpStyle: ConversationFollowUpStyle;
  /**
   * Confidence of this policy (0..1). This is separate from safety confidence.
   */
  policyConfidence: number;
  /**
   * Short rationale usable for logging/debugging.
   */
  policyRationale?: string;
}

export interface SafetyRoute {
  category: 'harmful' | 'caution' | 'normal';
  confidence: number;
  reason?: string;
  /** Intent classification for routing */
  intent: 'small_talk' | 'deep_talk' | 'straight_talk';
  /**
   * Optional retrieval-oriented classification hints emitted by the safety layer.
   * Retrieval can consume these directly, or fall back to heuristics when absent.
   */
  retrieval?: RetrievalClassificationHints;
  /**
   * Optional conversation policy computed by safety/intention layer.
   * When absent, downstream should fall back to heuristic momentum logic.
   */
  policy?: ConversationPolicy;
}

export interface AlignmentContext {
  confidence: number;
  strategy: 'aligned' | 'neutral' | 'unaligned';
  relatedTopic?: string;
}

export type ResponseMode =
  | 'blocked'
  | 'cautionPrompt'
  | 'aligned'
  | 'neutralSegue'
  | 'smallTalk'
  | 'straightTalk'
  | 'webSearch'
  | 'error';

export interface ChatTurn {
  role: 'user' | 'assistant';
  messageId: string;
  messageIndex: number;
  content: string;
  sources?: Record<string, { citationID: string; score?: number }>; // TODO: Confirm if this is how we want to represent citations we store in convo history
  timestamp: string;
}

export interface MemoryContext { // TODO: rethink this - I don't think the way it's currently defined it will be used anywhere
  recentTurns: ChatTurn[];
  summary?: string;
  entities?: string[];
  topics?: string[];
}

export interface RetrievalParams {
  tenantId: string;
  personaId?: string;
  topK?: number;
  filters?: Record<string, any>;
}

export interface ScoredResult<T> {
  item: T;
  score: number; // Normalized 0 to 1
  metadata?: Record<string, any>;
}

export interface Citation {
  /** Citation marker number (1, 2, 3...) */
  id: number;
  /** Unique chunk identifier */
  chunkId: string;
  /** Source file path or document name */
  source: string;
  /** Retrieval confidence score */
  score?: number;
  /** Parent document ID (for linking back to full document) */
  documentId?: string;
  /** Document title if available */
  title?: string;
  /** Topic/category of the chunk */
  topic?: string;
  /** Position of chunk within document */
  chunkIndex?: number;
  /** URL to source if available */
  url?: string;
  /** Brief excerpt from the chunk for reference */
  excerpt?: string;
  /** Page number within the source document */
  page?: number;
}

/**
 * Source format expected by the frontend.
 * This is the legacy format that the frontend components consume.
 */
export interface FrontendSource {
  /** Display name (document title or filename) */
  name: string;
  /** File path or S3 location */
  location: string;
  /** Relevance score */
  score: number;
  /** Citation number for [1], [2], etc. markers */
  source_number: number;
  /** Excerpt content for tooltip preview */
  chunk_content?: string;
  /** Page number within the document */
  page?: number;
  /** Presigned URL for direct document access (if available) */
  presigned_url?: string;
}

export interface QueryMetadata {
  safetyRoute?: SafetyRoute;
  responseMode?: ResponseMode;
  alignment?: AlignmentContext;
  comparisonRunId?: string;
  comparisonStreamId?: string;
  parentExchangeId?: string;
  parentChatId?: string;
  comparisonStreamLabel?: string;
  comparisonSettings?: Record<string, unknown>;
  /** Seed entity IDs for graph visualization (Searched) */
  seeds?: string[];
  /** Chunks selected for the prompt (Retrieved) */
  retrievedChunkIds?: string[];
  /** Chunks cited in the response (Used) */
  usedChunkIds?: string[];
  toolTrace?: unknown;
  useSynapse?: boolean;
  goal?: unknown;
}

export type AiPreset = 'default' | 'trusted_counsel' | 'everyday_coach' | 'deep_dive' | 'quick_answer' | 'open_exploration' | 'research';
export type GuidanceLens = 'practical' | 'integrated' | 'scripture_forward';
export type AnswerLength = 'short' | 'medium' | 'long' | 'comprehensive';
export type PersonalizationLevel = 'loose' | 'balanced' | 'required';

const AI_PRESET_VALUES = ['default', 'trusted_counsel', 'everyday_coach', 'deep_dive', 'quick_answer', 'open_exploration', 'research'] as const;
export const AI_PRESETS: AiPreset[] = [...AI_PRESET_VALUES];
const PERSONALIZATION_LEVELS: PersonalizationLevel[] = ['loose', 'balanced', 'required'];

export interface CompanionSettings {
  aiPreset: AiPreset;
  guidanceLens: GuidanceLens;
  enableAnswerExplanation: boolean;
  enableGroundingBadges: boolean;
  answerLength: AnswerLength;
  conversationStyle: string;
  citationsStrictness: string;
  youthMode: boolean;
  webSearchOnUnaligned: boolean;
  personalizationLevel: PersonalizationLevel;
  /** User override for goal pursuit (PRD §4 / §8). */
  goalPursuit?: GoalPursuitPreference;
  userOverride?: GoalUserOverride;
  customUserGoalId?: string;
}

export const DEFAULT_COMPANION_SETTINGS: CompanionSettings = {
  aiPreset: "open_exploration",
  guidanceLens: "practical",
  enableAnswerExplanation: true,
  enableGroundingBadges: false,
  answerLength: "medium",
  conversationStyle: "Neutral",
  citationsStrictness: "Balanced",
  youthMode: false,
  webSearchOnUnaligned: false,
  personalizationLevel: "balanced",
  goalPursuit: "default",
  userOverride: "default_org_goal",
};

export function mapAiPreset(val: any): AiPreset {
  if (typeof val === 'string' && AI_PRESETS.includes(val as AiPreset)) {
    return val as AiPreset;
  }

  return DEFAULT_COMPANION_SETTINGS.aiPreset;
}

export function mapPersonalizationLevel(val: any): PersonalizationLevel {
  if (typeof val === 'string' && PERSONALIZATION_LEVELS.includes(val as PersonalizationLevel)) {
    return val as PersonalizationLevel;
  }

  return DEFAULT_COMPANION_SETTINGS.personalizationLevel;
}

export function mapLegacyAnswerLength(val: any): AnswerLength {
  if (typeof val === 'string') {
    if (['short', 'medium', 'long', 'comprehensive'].includes(val)) {
      return val as AnswerLength;
    }
    return 'medium';
  }

  const num = Number(val);
  if (isNaN(num)) return 'medium';

  if (num <= 25) return 'short';
  if (num <= 50) return 'medium';
  if (num <= 75) return 'long';
  return 'comprehensive';
}

export const PROMPT_COMPONENT_TYPES = [
  'GLOBAL_CONSTRAINTS',
  'CORE_GUIDELINES',
  'DYNAMIC_CONTEXT',
  'SEPARATOR',
  'TEXT',
  'IDENTITY',
  'AUDIO_STYLE',
  'THEOLOGY',
  'GUIDANCE_SMALL_TALK',
  'GUIDANCE_STRAIGHT_TALK',
  'GUIDANCE_ALIGNED',
  'GUIDANCE_CAUTION',
  'GUIDANCE_NEUTRAL_SEGUE',
  'GUIDANCE_BLOCKED',
  'EMPTY',
  'AI_PRESET',
  'GUIDANCE_LENS',
  'ANSWER_LENGTH'
] as const;

export type PromptComponentType = (typeof PROMPT_COMPONENT_TYPES)[number];

export interface PromptComponent {
  id: string;
  type: string;
  content: string;
  isActive: boolean;
}

export interface User {
  userId: string;
  email: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Which surface the feedback was given from. Both surfaces write to the same
 * chat-history table under chatType `digital_human`, so without this attribute
 * avatar and chatbot feedback are indistinguishable downstream.
 */
export type FeedbackMode = 'AVATAR' | 'TEXT';

export type FeedbackCategoryId =
  | 'WRONG_ANSWER'
  | 'MISHEARD_ME'
  | 'TOO_SLOW'
  | 'AUDIO_GLITCH'
  | 'VISUAL_LIPSYNC'
  | 'INTERRUPTED_ME'
  | 'OFF_CHARACTER'
  | 'MISSING_CITATION';

/**
 * Failure taxonomy captured at click time on a thumbs-down. Delivery failures
 * (latency, audio, lipsync, barge-in) and content failures (wrong answer,
 * bad citation) are otherwise indistinguishable in a freeform comment, which
 * makes negative ratings unusable as an eval signal.
 */
export const FEEDBACK_CATEGORIES: ReadonlyArray<{
  id: FeedbackCategoryId;
  label: string;
  modes: ReadonlyArray<FeedbackMode>;
}> = [
  { id: 'WRONG_ANSWER',     label: 'Wrong or made-up',   modes: ['AVATAR', 'TEXT'] },
  { id: 'MISSING_CITATION', label: 'Citation problem',   modes: ['AVATAR', 'TEXT'] },
  { id: 'OFF_CHARACTER',    label: 'Off-character',      modes: ['AVATAR', 'TEXT'] },
  { id: 'TOO_SLOW',         label: 'Too slow',           modes: ['AVATAR', 'TEXT'] },
  { id: 'MISHEARD_ME',      label: 'Misheard me',        modes: ['AVATAR'] },
  { id: 'AUDIO_GLITCH',     label: 'Audio glitch',       modes: ['AVATAR'] },
  { id: 'VISUAL_LIPSYNC',   label: 'Lipsync / visual',   modes: ['AVATAR'] },
  { id: 'INTERRUPTED_ME',   label: 'Talked over me',     modes: ['AVATAR'] },
] as const;

export const FEEDBACK_CATEGORY_IDS: ReadonlyArray<FeedbackCategoryId> =
  FEEDBACK_CATEGORIES.map(c => c.id);

export const feedbackCategoriesForMode = (mode: FeedbackMode) =>
  FEEDBACK_CATEGORIES.filter(c => c.modes.includes(mode));

export const feedbackCategoryLabel = (id: string): string =>
  FEEDBACK_CATEGORIES.find(c => c.id === id)?.label ?? id;

/** Per-turn latency telemetry, promoted out of the stringified `metadata` blob. */
export interface FeedbackTurnMetrics {
  uploadBytes?: number;
  uploadDurationMs?: number;
  transcriptionMs?: number;
  llmFirstTokenMs?: number;
  ttsFirstByteMs?: number;
  firstAudioChunkMs?: number;
  assetFinalizationMs?: number;
  totalTurnMs?: number;
}

/** Captured at feedback time so "it was slow"/"it looked broken" is reproducible. */
export interface FeedbackClientContext {
  userAgent?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
  viewport?: string;
  appVersion?: string;
  hostname?: string;
}

export interface FeedbackEntry {
  exchangeId: string;
  chatId: string;
  userId?: string;
  userEmail?: string;
  personaId?: string;
  personaName?: string;
  timestamp: string;
  question: string;
  answer: string;
  rating: string;
  comment?: string;
  githubIssueNumber?: number;
  githubIssueUrl?: string;
  feedbackMode?: FeedbackMode;
  feedbackCategory?: FeedbackCategoryId[];
  turnMetrics?: FeedbackTurnMetrics;
  clientContext?: FeedbackClientContext;
  category?: string;
  tenantId?: string;
}

export interface FeedbackSummary {
  totalPositive: number;
  totalNegative: number;
  personaStats: Record<string, { positive: number, negative: number, personaName?: string }>;
  modeStats?: Record<FeedbackMode, { positive: number, negative: number }>;
  categoryStats?: Record<string, number>;
}
